import { useEffect, useMemo, useState, type SyntheticEvent } from 'react';
import { WorkoutData } from '../../../models/training.models';
import useService from '../../../hooks/service.hook';
import EquipmentService from '../../../services/equipments.service';
import AssignedTrainingsService from '../../../services/assigned-trainings.service';
import { useSnackbar } from 'notistack';

export interface EquipmentOption {
  id: number;
  name: string;
}

export interface UseLinkEquipmentParams {
  workoutData: WorkoutData | null | undefined;
  trainingId: number;
  trainingType: 'athlete' | 'coach' | 'guardian';
  onEquipmentLinked?: () => void;
}

export function canEditTrainingEquipment(
  trainingId: number,
  trainingType: 'athlete' | 'coach' | 'guardian',
  workoutData: WorkoutData | null | undefined
): boolean {
  const trainingIdValid = Number.isFinite(trainingId) && trainingId > 0;
  const isGuardian = trainingType === 'guardian';
  const isAssignedParticipant = trainingType === 'athlete' || (trainingType === 'coach' && !!workoutData);
  return trainingIdValid && !isGuardian && isAssignedParticipant;
}

export function useLinkEquipment({ workoutData, trainingId, trainingType, onEquipmentLinked }: UseLinkEquipmentParams) {
  const trainingIdValid = Number.isFinite(trainingId) && trainingId > 0;
  const canEditEquipment = canEditTrainingEquipment(trainingId, trainingType, workoutData);
  const equipmentService = useService(EquipmentService);
  const assignedTrainingsService = useService(AssignedTrainingsService);
  const { enqueueSnackbar } = useSnackbar();
  const [options, setOptions] = useState<EquipmentOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [linking, setLinking] = useState(false);

  const resolvedLinkedEquipmentId = useMemo(() => {
    const raw = workoutData?.equipment?.id;
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [workoutData?.equipment?.id]);

  useEffect(() => {
    if (!canEditEquipment) return;
    setLoadingOptions(true);
    equipmentService
      .getEquipments()
      .then((res) => {
        const raw = res.data.data;
        const list = Array.isArray(raw) ? raw : raw?.results ?? [];
        setOptions(
          (list as { id?: number | string; name?: string; equipment_name?: string }[]).map((item) => {
            const idNum = Number(item.id);
            return {
              id: Number.isFinite(idNum) ? idNum : 0,
              name: item.name ?? item.equipment_name ?? '',
            };
          })
        );
      })
      .catch(() => setOptions([]))
      .finally(() => setLoadingOptions(false));
  }, [equipmentService, canEditEquipment]);

  const autocompleteValue = useMemo((): EquipmentOption | null => {
    if (resolvedLinkedEquipmentId == null) return null;
    const fromList = options.find((o) => o.id === resolvedLinkedEquipmentId);
    if (fromList) return fromList;
    const name = workoutData?.equipment?.name?.trim();
    return {
      id: resolvedLinkedEquipmentId,
      name: name || `Equipment #${resolvedLinkedEquipmentId}`,
    };
  }, [resolvedLinkedEquipmentId, workoutData?.equipment?.name, options]);

  const autocompleteOptions = useMemo(() => {
    if (!canEditEquipment) {
      if (autocompleteValue) return [autocompleteValue];
      return [];
    }
    if (!autocompleteValue) return options;
    if (options.some((o) => o.id === autocompleteValue.id)) return options;
    return [autocompleteValue, ...options];
  }, [canEditEquipment, options, autocompleteValue]);

  const handleEquipmentChange = async (_: SyntheticEvent, newValue: EquipmentOption | null) => {
    if (!canEditEquipment || !newValue?.id || !trainingIdValid) return false;
    if (resolvedLinkedEquipmentId != null && newValue.id === resolvedLinkedEquipmentId) return false;
    setLinking(true);
    try {
      await assignedTrainingsService.linkEquipmentToTraining(String(trainingId), { equipmentId: newValue.id });
      enqueueSnackbar('Equipment linked to training.', {
        variant: 'success',
        autoHideDuration: 3000,
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
      onEquipmentLinked?.();
      return true;
    } catch {
      enqueueSnackbar('Could not link equipment. Please try again.', {
        variant: 'error',
        autoHideDuration: 4000,
        anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
      });
      return false;
    } finally {
      setLinking(false);
    }
  };

  return {
    canEditEquipment,
    options: autocompleteOptions,
    autocompleteValue,
    loadingOptions,
    linking,
    handleEquipmentChange,
    resolvedLinkedEquipmentId,
  };
}
